import { useAuth } from "@/context/AuthContext";
import { fetchMetrics, type MetricsData, type MetricsFilters } from "@/services/documentsApi";
import { useQuery } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Hook to get access token for API calls
 */
async function getAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) {
    throw new Error("No access token available");
  }
  return accessToken;
}

export interface UseMetricsOptions {
  /** Refetch interval in milliseconds. Default: 10000 (10 seconds) */
  refetchInterval?: number;
  /** Optional date filters for completed/review counts (in_process is always unfiltered) */
  filters?: MetricsFilters;
}

/**
 * Hook to fetch document metrics with automatic polling
 *
 * Returns aggregated counts of documents by status category:
 * - total_completed: Documents with COMPLETE status (filtered by date if provided)
 * - in_process: Documents with PENDING, UPLOADED, or PROCESSING status (NEVER filtered by date)
 * - requires_review: Documents with REQUIRES_REVIEW status (filtered by date if provided)
 *
 * Note: in_process is intentionally NOT filtered by date - users always need to see
 * their total queue depth regardless of the date filter they're viewing.
 *
 * @param options - Configuration options
 * @returns Query result with metrics data, loading state, and error
 */
export function useMetrics(options?: UseMetricsOptions) {
  const { isAuthenticated } = useAuth();
  const refetchInterval = options?.refetchInterval ?? 10000; // Default 10 seconds
  const filters = options?.filters;

  return useQuery<MetricsData, Error>({
    queryKey: ["metrics", filters?.startDate, filters?.endDate],
    queryFn: async () => {
      const token = await getAccessToken();
      return fetchMetrics(token, filters);
    },
    enabled: isAuthenticated,
    refetchInterval, // Auto-refresh metrics
    staleTime: 5000, // Consider data stale after 5 seconds
  });
}
